/**
 * API client for the Book4 backend (web).
 * Re-exports from @book4/api-client and adds Next.js server cookie support for getToken/getCustomerToken.
 */

import {
  getApiUrl as getApiUrlFromClient,
  fetchApi as fetchApiFromClient,
  getToken as getTokenFromClient,
  getCustomerToken as getCustomerTokenFromClient,
  setCustomerToken as setCustomerTokenFromClient,
  clearCustomerToken as clearCustomerTokenFromClient,
  setToken as setTokenFromClient,
  clearToken as clearTokenFromClient,
  getCurrentUser as getCurrentUserFromClient,
  getCurrentCustomer as getCurrentCustomerFromClient,
  getLinkedCustomerToken as getLinkedCustomerTokenFromClient,
  getLinkedBusinessToken as getLinkedBusinessTokenFromClient,
  fetchLinkedToken,
  confirmAccountLink as confirmAccountLinkFromClient,
  type ConfirmAccountLinkResult,
  type LinkedTokenResult,
  isCustomerToken,
  getJwtRole,
  SWITCH_CUSTOMER_TOKEN_KEY,
} from "@book4/api-client";
import type { AuthUser, CustomerProfile } from "@book4/types";

export { isCustomerToken, getJwtRole, SWITCH_CUSTOMER_TOKEN_KEY };
export type { AuthUser, CustomerProfile, ConfirmAccountLinkResult, LinkedTokenResult };
export type { AccountLinkInfo, AccountLinkStatus } from "@book4/types";

export function getApiUrl(): string {
  return getApiUrlFromClient();
}

/** Get token: from Next.js cookies on server, from api-client (localStorage/cookie) on client. */
export async function getToken(): Promise<string | null> {
  if (typeof window !== "undefined") return getTokenFromClient();
  const { cookies } = await import("next/headers");
  const c = await cookies();
  return c.get("token")?.value ?? null;
}

/** Get customer token: from Next.js cookies on server, from api-client on client. */
export async function getCustomerToken(): Promise<string | null> {
  if (typeof window !== "undefined") return getCustomerTokenFromClient();
  const { cookies } = await import("next/headers");
  const c = await cookies();
  return c.get("customer_token")?.value ?? null;
}

export function setCustomerToken(token: string): void {
  if (typeof window === "undefined") return;
  setCustomerTokenFromClient(token);
}

export function clearCustomerToken(): void {
  if (typeof window === "undefined") return;
  clearCustomerTokenFromClient();
}

export function setToken(token: string): void {
  if (typeof window === "undefined") return;
  setTokenFromClient(token);
}

export function clearToken(): void {
  if (typeof window === "undefined") return;
  clearTokenFromClient();
}

export async function fetchApi<T>(
  path: string,
  options: RequestInit & { token?: string | null } = {}
): Promise<T> {
  return fetchApiFromClient<T>(path, options);
}

export async function getCurrentCustomer(): Promise<CustomerProfile | null> {
  const token = await getCustomerToken();
  if (!token) return null;
  try {
    const profile = await fetchApiFromClient<CustomerProfile>("/api/auth/me", { token });
    return profile?.role === "CUSTOMER" ? profile : null;
  } catch {
    return null;
  }
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  const token = await getToken();
  if (!token) return null;
  try {
    return await fetchApiFromClient<AuthUser>("/api/auth/me", {
      token,
      cache: "no-store" as RequestCache,
    });
  } catch {
    return null;
  }
}

export async function getLinkedCustomerToken(): Promise<string | null> {
  const token = await getToken();
  if (!token) return null;
  try {
    const data = await fetchApiFromClient<{ token: string }>("/api/auth/link/customer-token", {
      token,
    });
    return data?.token ?? null;
  } catch {
    return null;
  }
}

export async function getLinkedBusinessToken(): Promise<string | null> {
  const token = await getCustomerToken();
  if (!token) return null;
  try {
    const data = await fetchApiFromClient<{ token: string }>("/api/auth/link/business-token", {
      token,
    });
    return data?.token ?? null;
  } catch {
    return null;
  }
}

/** Business session to customer: the token, or why the switch was refused (refusedMessage). */
export async function requestLinkedCustomerToken(): Promise<LinkedTokenResult> {
  const token = await getToken();
  if (!token) return { token: null, refusedMessage: null };
  return fetchLinkedToken("/api/auth/link/customer-token", token);
}

/** Customer session to business: the token, or why the switch was refused (refusedMessage). */
export async function requestLinkedBusinessToken(): Promise<LinkedTokenResult> {
  const token = await getCustomerToken();
  if (!token) return { token: null, refusedMessage: null };
  return fetchLinkedToken("/api/auth/link/business-token", token);
}

/**
 * Link the signed in account (side) with the other one by entering the other side's password
 * (POST /auth/link/confirm). side "business" sends the booking account password; "customer" sends
 * the business login password.
 */
export async function confirmAccountLink(side: "business" | "customer", password: string): Promise<ConfirmAccountLinkResult> {
  const token = side === "business" ? await getToken() : await getCustomerToken();
  if (!token) return { ok: false, status: 401, error: "Please sign in again." };
  return confirmAccountLinkFromClient(password, token);
}

/**
 * Send a new verification email for the signed in account (side), using the existing resend routes
 * (POST /auth/resend-verification and /auth/customer/resend-verification).
 */
export async function resendVerificationEmail(
  side: "business" | "customer"
): Promise<{ ok: true; alreadyVerified: boolean } | { ok: false; error: string | null }> {
  const token = side === "business" ? await getToken() : await getCustomerToken();
  if (!token) return { ok: false, error: null };
  const path = side === "business" ? "/api/auth/resend-verification" : "/api/auth/customer/resend-verification";
  try {
    const res = await fetch(`${getApiUrl()}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      credentials: "include",
    });
    const data = (await res.json().catch(() => ({}))) as { error?: string; alreadyVerified?: boolean };
    if (!res.ok) return { ok: false, error: data.error ?? null };
    return { ok: true, alreadyVerified: data.alreadyVerified === true };
  } catch {
    return { ok: false, error: null };
  }
}
